// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Button,
  ButtonGroup,
  Card,
  Description,
  InlineAlert,
  InputCheckbox,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  SmSpacing,
  Tag,
  Toast,
  ToastContext,
} from '@alto/design-system';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Form, Formik, type FormikProps } from 'formik';
import React, { useContext } from 'react';
import { completeOnboarding } from '~shared/actions/auth';
import { closeModal, previousModalStep } from '~shared/actions/modal';
// eslint-disable-next-line import/no-deprecated
import { updateUser } from '~shared/actions/users';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import getUserPhiAuth from '~shared/features/users/selectors/getUserPhiAuth';
import {
  type CommunicationPreferencesFormFields,
  PREFERRED_COMMUNICATION_OPTIONS,
  SECONDARY_COMMUNICATION_PREFERENCE_OPTIONS,
  SECURE_MESSAGE,
  validate,
} from '~shared/helpers/communicationPreferences';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getUserError from '~shared/selectors/ui/errors/getUserError';
import getUpdateUserLoading from '~shared/selectors/ui/loading/getUpdateUserLoading';
import { useDispatchShared, useSelectorShared } from '~shared/store';

export const showNotificationType = (preferredCommunication: string | undefined) =>
  preferredCommunication === SECURE_MESSAGE;

type Props = {
  isOnboarding?: boolean;
};

export const CommunicationPreferencesForm = ({ isOnboarding = false }: Props) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { closeActionSheet } = useContext(ActionSheetContext);
  const userError = useSelectorShared(getUserError);
  const updateUserLoading = useSelectorShared(getUpdateUserLoading);
  const user = useSelectorShared(getCurrentUser);
  const user_phi_data = useSelectorShared(getUserPhiAuth);

  const initialValues: CommunicationPreferencesFormFields = {
    preferred_communication: isOnboarding ? SECURE_MESSAGE : user?.preferred_communication || SECURE_MESSAGE,
    secondary_communication_preference: user?.secondary_communication_preference,
    phi_auth_data: {
      sms_authorized: isOnboarding ? true : (user_phi_data?.sms_authorized ?? false),
      email_authorized: isOnboarding ? true : (user_phi_data?.email_authorized ?? false),
      push_authorized: isOnboarding ? true : (user_phi_data?.push_authorized ?? false),
    },
  };

  const goBack = () => {
    if (isOnboarding) {
      dispatch(previousModalStep('ONBOARDING_MODAL'));
    } else {
      closeActionSheet();
    }
  };

  const onSubmit = async (data: CommunicationPreferencesFormFields) => {
    const { preferred_communication, secondary_communication_preference, phi_auth_data } = data;
    // filter out hidden fields
    const user: Partial<CommunicationPreferencesFormFields> = {
      preferred_communication,
    };

    if (showNotificationType(preferred_communication)) {
      user.secondary_communication_preference = secondary_communication_preference;
    }

    try {
      // eslint-disable-next-line import/no-deprecated
      const { success } = await dispatch(updateUser({ ...user, phi_auth_data: JSON.stringify(phi_auth_data) }));
      if (!success) return;

      if (isOnboarding) {
        dispatch(closeModal('ONBOARDING_MODAL'));
        dispatch(completeOnboarding());
        trackEvent({ event: EVENTS.ONBOARDING__COMPLETED });
      } else {
        closeActionSheet();
        addToast(<Toast>Successfully updated communication preferences</Toast>);
      }
    } catch {
      addToast(<Toast variant="error">{`${DEFAULT_ERROR_ALERT_TITLE}. ${DEFAULT_ERROR_ALERT_MESSAGE}`}</Toast>);
    }
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validate}
    >
      {({
        values,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
      }: FormikProps<CommunicationPreferencesFormFields>) => {
        return (
          <Form noValidate>
            <LgPadding topPadding={SPACING.STATIC.NONE}>
              {isOnboarding ? (
                <>
                  <LgSpacing />
                  <InlineAlert>We’ll send you medication and delivery updates with secure in-app messages.</InlineAlert>
                </>
              ) : (
                <>
                  <Body>How would you like Alto to contact you about your prescriptions?</Body>
                  <SmSpacing />
                </>
              )}
              {userError ? (
                <>
                  <InlineAlert type="warning">
                    <Body>{userError.message}</Body>
                  </InlineAlert>
                  <SmSpacing />
                </>
              ) : null}
              {!isOnboarding ? (
                <Card title="Preferred Communication">
                  {errors.preferred_communication ? (
                    <>
                      <InlineAlert type="error">
                        <Description>{errors.preferred_communication}</Description>
                      </InlineAlert>
                      <SmSpacing />
                    </>
                  ) : undefined}
                  <InputRadioGroup
                    onValueChange={handleChange('preferred_communication')}
                    defaultValue={values.preferred_communication || undefined}
                  >
                    {PREFERRED_COMMUNICATION_OPTIONS.map((option, i) => (
                      <InputRadio
                        label={option.label}
                        value={option.value}
                        key={`preferred_communication-${option.value}`}
                        tag={
                          i === 0 ? (
                            <Tag
                              label="recommended"
                              type="success"
                            />
                          ) : undefined
                        }
                      />
                    ))}
                  </InputRadioGroup>
                </Card>
              ) : null}
              {showNotificationType(values.preferred_communication) ? (
                <>
                  <LgSpacing />
                  <Card title="Notification Type">
                    {errors.secondary_communication_preference ? (
                      <>
                        <InlineAlert type="error">
                          <Description>{errors.secondary_communication_preference}</Description>
                        </InlineAlert>
                        <SmSpacing />
                      </>
                    ) : undefined}
                    <LgPadding
                      topPadding={SPACING.STATIC.NONE}
                      bottomPadding={SPACING.STATIC.NONE}
                    >
                      <Description>
                        We’ll also notify you with a push notification and either text or email.
                      </Description>
                    </LgPadding>
                    <SmSpacing />
                    <InputRadioGroup
                      onValueChange={handleChange('secondary_communication_preference')}
                      defaultValue={values.secondary_communication_preference || undefined}
                    >
                      {SECONDARY_COMMUNICATION_PREFERENCE_OPTIONS.map((option, i) => (
                        <InputRadio
                          label={option.label}
                          value={option.value}
                          key={`secondary-preferred_communication-${option.value}`}
                          tag={
                            i === 0 ? (
                              <Tag
                                label="recommended"
                                type="success"
                              />
                            ) : undefined
                          }
                        />
                      ))}
                    </InputRadioGroup>
                  </Card>
                </>
              ) : null}
              <LgSpacing />
              <Card title="Can we include personal health info in messages we send you?">
                <LgPadding
                  topPadding={SPACING.STATIC.NONE}
                  bottomPadding={SPACING.STATIC.SM}
                >
                  <Description>
                    This allows our team to provide more detail when we communicate with you about your medication or
                    orders.
                  </Description>
                </LgPadding>
                <InputCheckbox
                  name="sms_authorized"
                  label="Include in text messages/SMS"
                  onPress={() => {
                    setFieldValue('phi_auth_data.sms_authorized', !values.phi_auth_data.sms_authorized);
                  }}
                  value={values.phi_auth_data.sms_authorized}
                />
                <InputCheckbox
                  name="email_authorized"
                  label="Include in emails"
                  onPress={() => {
                    setFieldValue('phi_auth_data.email_authorized', !values.phi_auth_data.email_authorized);
                  }}
                  value={values.phi_auth_data.email_authorized}
                />
                <InputCheckbox
                  name="push_authorized"
                  label="Include in push notifications"
                  onPress={() => {
                    setFieldValue('phi_auth_data.push_authorized', !values.phi_auth_data.push_authorized);
                  }}
                  value={values.phi_auth_data.push_authorized}
                />
              </Card>
              <LgSpacing />
              <ButtonGroup
                justifyButtons="right"
                inline
                buttons={[
                  <Button
                    key="dismiss-communication-preferences"
                    label={isOnboarding ? 'Back' : 'Cancel'}
                    onPress={goBack}
                    type="tertiary"
                  />,
                  <Button
                    key="submit-communication-preferences"
                    loading={updateUserLoading}
                    loadingLabel="Submitting"
                    label={isOnboarding ? 'Complete profile' : 'Update'}
                    onPress={handleSubmit}
                  />,
                ]}
              />
            </LgPadding>
          </Form>
        );
      }}
    </Formik>
  );
};
